import { useId, useRef, useState } from "react";
import { Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from "@mui/material";
import MoreHorizRounded from "@mui/icons-material/MoreHorizRounded";
import SwapHorizRounded from "@mui/icons-material/SwapHorizRounded";
import AcUnitRounded from "@mui/icons-material/AcUnitRounded";
import LockOpenRounded from "@mui/icons-material/LockOpenRounded";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import FactCheckOutlined from "@mui/icons-material/FactCheckOutlined";
import HighlightOffRounded from "@mui/icons-material/HighlightOffRounded";
import { useTranslation } from "react-i18next";
import { MonitorButton } from "./MonitorControls";
import PromotionFreeze from "./PromotionFreeze";

export default function PromotionPackageActions({ row, spaceId, freezeControls, topicAction, retirementAction, discardAction, executionAction, compact = false }) {
  const { t } = useTranslation("freigabe");
  const id = useId();
  const trigger = useRef(null);
  const [anchor, setAnchor] = useState(null);
  const [openingDialog, setOpeningDialog] = useState(false);
  const choose = callback => { setOpeningDialog(true); setAnchor(null); callback(trigger.current); };
  return <PromotionFreeze row={row} spaceId={spaceId} {...freezeControls} returnFocusRef={trigger} renderAction={freeze => {
    const item = topicAction?.item || retirementAction?.item;
    const topicLabel = item?.pending ? "packageTopic.retry" : item?.topicId || item?.membershipState === "mixed" ? "packageTopic.change" : "packageTopic.assign";
    const retireLabel = item?.pendingRetirement ? "packageRetirement.retry" : "packageRetirement.remove";
    const topicDisabled = topicAction?.disabled || !!item?.pendingRetirement;
    const retirementDisabled = retirementAction?.disabled || (item?.pending && !item.pendingRetirement);
    const available = [(topicAction && item) && !topicDisabled, (retirementAction && item) && !retirementDisabled,
      executionAction && !executionAction.disabled, freeze.canManage && !freeze.disabled, discardAction && !discardAction.disabled];
    const openMenu = event => { setOpeningDialog(false); setAnchor(event.currentTarget); };
    if (!(topicAction && item) && !(retirementAction && item) && !freeze.canManage && !discardAction && !executionAction) return null;
    return <>
      {compact
        ? <Tooltip title={t("packageActions.label")}><span className="promotion-package-menu-wrap">
            <IconButton ref={trigger} id={`${id}-trigger`} size="small" className="promotion-package-menu-trigger promotion-package-menu-trigger--compact"
              aria-haspopup="menu" aria-expanded={Boolean(anchor)} aria-controls={anchor ? `${id}-menu` : undefined}
              aria-label={t("packageActions.named", { id: row.packageId })} disabled={!available.some(Boolean)}
              onClick={openMenu}><MoreHorizRounded/></IconButton></span></Tooltip>
        : <MonitorButton ref={trigger} id={`${id}-trigger`} className="promotion-package-menu-trigger" startIcon={<MoreHorizRounded/>}
            aria-haspopup="menu" aria-expanded={Boolean(anchor)} aria-controls={anchor ? `${id}-menu` : undefined}
            aria-label={t("packageActions.named", { id: row.packageId })} disabled={!available.some(Boolean)}
            onClick={openMenu}>{t("packageActions.label")}</MonitorButton>}
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)} disableRestoreFocus={openingDialog}
        PaperProps={{ className: "iio iio-monitor-menu promotion-package-menu" }}
        MenuListProps={{ id: `${id}-menu`, "aria-labelledby": `${id}-trigger` }}>
        {executionAction && <MenuItem disabled={executionAction.disabled} aria-haspopup="dialog" onClick={() => choose(executionAction.onClick)}>
          <ListItemIcon><FactCheckOutlined/></ListItemIcon><ListItemText>{t("packageExecution.title")}</ListItemText>
        </MenuItem>}
        {topicAction && item && <MenuItem disabled={topicDisabled} aria-haspopup="dialog"
          aria-label={t("packageTopic.manageNamed", { name: item.packageName || item.packageId, action: t(topicLabel) })}
          onClick={() => choose(button => topicAction.onClick(item, button))}>
          <ListItemIcon><SwapHorizRounded/></ListItemIcon><ListItemText>{t(topicLabel)}</ListItemText>
        </MenuItem>}
        {freeze.canManage && <MenuItem disabled={freeze.disabled} aria-haspopup="dialog"
          aria-label={t(`freeze.${freeze.label}Named`, { name: row.packageName || row.packageId })} onClick={() => choose(freeze.open)}>
          <ListItemIcon>{freeze.frozen ? <LockOpenRounded/> : <AcUnitRounded/>}</ListItemIcon><ListItemText>{t(`freeze.${freeze.label}`)}</ListItemText>
        </MenuItem>}
        {discardAction && <MenuItem disabled={discardAction.disabled} aria-haspopup="dialog"
          aria-label={t("promotion.discardNamed", { name: row.packageName || row.packageId })} onClick={() => choose(discardAction.onClick)}>
          <ListItemIcon><HighlightOffRounded/></ListItemIcon><ListItemText>{t("promotion.discard")}</ListItemText>
        </MenuItem>}
        {retirementAction && item && <Divider/>}
        {retirementAction && item && <MenuItem className="promotion-package-menu-remove" disabled={retirementDisabled} aria-haspopup="dialog"
          aria-label={t(item.pendingRetirement ? "packageRetirement.retryNamed" : "packageRetirement.removeNamed", { id: item.packageId })}
          onClick={() => choose(button => retirementAction.onClick(item, button))}>
          <ListItemIcon><DeleteOutlineRounded/></ListItemIcon><ListItemText>{t(retireLabel)}</ListItemText>
        </MenuItem>}
      </Menu>
    </>;
  }}/>;
}
